import { useDateTimeFilter, type DateTimeFilter } from '~/presentation/ui';

import {
  COMPARE_DATE_TIME_FILTERS_MAP,
  CompareDateTimePickerAlias,
  PREVIOUS_PERIOD_FILTER,
} from '~/presentation/ui/components/GTimePicker/constants';
import { createContext, useCallback, useContext, useState } from 'react';
import { dayjs, dayjsTz } from '~/utils/dayjs';

export interface ContextType {
  dateTimePicked: DateTimeFilter;
  compareDateTimePicked: DateTimeFilter;
  setDateTimePicked: (value: DateTimeFilter) => void;
  setCompareDateTimePicked: (value: DateTimeFilter) => void;
  resetDateVersion: () => void;
}

export const DateTimePickerContext = createContext<ContextType>({
  dateTimePicked: {
    since: dayjsTz().valueOf(),
    until: dayjsTz().valueOf(),
    title: 'Today',
    alias: 'today',
  },
  compareDateTimePicked: PREVIOUS_PERIOD_FILTER,
  setDateTimePicked: () => {},
  setCompareDateTimePicked: () => {},
  resetDateVersion: () => {},
});

export const DateTimePickerProvider = ({
  children,
  initDate,
  initDateCompare,
}: {
  children: React.ReactNode;
  initDate?: DateTimeFilter;
  initDateCompare?: DateTimeFilter;
}) => {
  const [dateTimePicked, setDateTimePicked] = useState(
    initDate || {
      since: dayjsTz().set('hour', 0).valueOf(),
      until: dayjsTz().set('hour', 0).valueOf(),
      title: 'Today',
      alias: 'today',
    },
  );
  const { DATE_TIME_FILTERS } = useDateTimeFilter();

  const getCompareDateTimePicked = useCallback(
    (alias: CompareDateTimePickerAlias, source = dateTimePicked) => {
      return {
        ...PREVIOUS_PERIOD_FILTER,
        ...COMPARE_DATE_TIME_FILTERS_MAP[alias]({
          since: source.since,
          until: source.until,
          alias: source.alias,
        }),
      };
    },
    [dateTimePicked],
  );

  const [compareDateTimePicked, setCompareDateTimePicked] = useState(
    initDateCompare || getCompareDateTimePicked(CompareDateTimePickerAlias.PREVIOUS_PERIOD),
  );

  const updateCompareDateTimePicked = useCallback(
    (source: DateTimeFilter) => {
      if (
        compareDateTimePicked.alias === 'custom' ||
        compareDateTimePicked.alias === CompareDateTimePickerAlias.NO_COMPARISON
      )
        return;
      setCompareDateTimePicked(
        getCompareDateTimePicked(compareDateTimePicked.alias as CompareDateTimePickerAlias, source),
      );
    },
    [compareDateTimePicked, getCompareDateTimePicked],
  );

  const onSetDateTimePicked = useCallback(
    (value: DateTimeFilter) => {
      setDateTimePicked(value);
      updateCompareDateTimePicked(value);
    },
    [updateCompareDateTimePicked],
  );

  const resetDateVersion = () => {
    const since = dayjs(dateTimePicked.since).startOf('day').valueOf();
    const until = dayjs(dateTimePicked.until).startOf('day').valueOf();
    const dateFilter = DATE_TIME_FILTERS.find(
      (filter) => filter.since === since && filter.until === until,
    );

    onSetDateTimePicked({
      ...dateTimePicked,
      isVersion: false,
      versionId: undefined,
      isCurrentVersion: false,
      description: '',
      title: dateFilter?.title || dateTimePicked.title,
      alias: dateFilter?.alias || 'custom',
    });
  };

  return (
    <DateTimePickerContext.Provider
      value={{
        dateTimePicked,
        setDateTimePicked: onSetDateTimePicked,
        compareDateTimePicked,
        setCompareDateTimePicked,
        resetDateVersion,
      }}
    >
      {children}
    </DateTimePickerContext.Provider>
  );
};

export const useDateTimePickerContext = () => {
  const context = useContext(DateTimePickerContext);
  if (!context) {
    throw new Error('useDateTimePickerContext must be used within a DateTimePickerProvider');
  }
  return context;
};
